// Import style for web component
const styles = require( './web-component-style.scss' );

// Base template
const template = document.createElement( 'template' );
template.innerHTML = `
    <style>

        :host {
            display: flex;
        }

        :host(:first-child) .option {
        	border-top-left-radius: var(--uap-border-radius);
        	border-top-right-radius: var(--uap-border-radius);
        }

        :host(:last-child) .option {
        	border-bottom-left-radius: var(--uap-border-radius);
        	border-bottom-right-radius: var(--uap-border-radius);
        }

        :host(:not(:last-child)) .option {
        	border-bottom: 1px solid var(--uap-border-color-gray);
        }
        
        ${styles}

    </style>

    <div class="option"></div>
`;

/**
 * Select <uo-select-option>
 *
 * Attributes:
 */
class AutomatorSelectOption extends HTMLElement {
	/**
	 * Defines observed attributes
	 *
	 * @return {Array} Observed attributes
	 */
	static get observedAttributes() {
		return [ 'value', 'text', 'selected' ];
	}

	/**
	 * Creates the component
	 *
	 * @return {undefined}
	 */
	constructor() {
		super();

		// Attach shadow
		this.attachShadow( {
			mode: 'open',
		} );

		// Create alert
		this.shadowRoot.appendChild( template.content.cloneNode( true ) );
	}

	/**
	 * Callback invoked when the attribute changes
	 *
	 * @param {String} attrName Attribute name
	 * @param {String} oldValue The old value
	 * @param {String} newValue The new value
	 * @return {undefined}
	 */
	attributeChangedCallback( attrName, oldValue, newValue ) {
		// Check if the old value is different than the new one
		// This will prevent an infinite loop
		if ( oldValue !== newValue ) {
			// Check if it's a boolean attribute
			if ( [ 'selected' ].includes( attrName ) ) {
				this[attrName] = this.hasAttribute( attrName );
			} else {
				this[attrName] = this.getAttribute( attrName );
			}
		}
	}

	/**
	 * Sets the value of the option
	 *
	 * @return {String} The value
	 */
	get value() {
		return this.getAttribute( 'value' );
	}

	/**
	 * Sets the value of the option
	 *
	 * @param {String} value The value
	 * @return {undefined}
	 */
	set value( value ) {
		// Set value in the host
		this.setAttribute( 'value', value );
	}

	/**
	 * Sets the text of the option
	 *
	 * @return {String} The text
	 */
	get text() {
		return this.getAttribute( 'text' );
	}

	/**
	 * Sets the text of the option
	 *
	 * @param {String} value The text
	 * @return {undefined}
	 */
	set text( value ) {
		// Set text in the host
		this.setAttribute( 'text', value );

		// Set the text as the option content
		this.$option.innerText = value;
	}

	/**
	 * Determines if the option is selected by checking if the element has
	 * the attribute "selected"
	 *
	 * @return {Boolean} True if it's selected
	 */
	get selected() {
		return this.hasAttribute( 'selected' );
	}

	/**
	 * Sets the status of the selected attribute
	 *
	 * @param {Boolean} value True if the option should be selected
	 * @return {undefined}
	 */
	set selected( value ) {
		if ( value ) {
			// Set selected status in the host
			this.setAttribute( 'selected', '' );

			// Add selected attribute to the main element
			this.$option.setAttribute( 'selected', '' );
		} else {
			// Set selected status in the host
			this.removeAttribute( 'selected' );

			// Remove selected attribute to the main element
			this.$option.removeAttribute( 'selected' );
		}
	}

	/**
	 * Returns the option node
	 *
	 * @return {Node} The option
	 */
	get $option() {
		return this.shadowRoot.querySelector( '.option' );
	}
}

customElements.define( 'uo-select-option', AutomatorSelectOption );
